import React, { useState, useEffect, useCallback } from 'react';
import { dossiersAPI } from '../services/api';
import SendDocumentsModal from './SendDocumentsModal';

const API_URL = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
const GOLD = 'var(--brand-primary, #d9a847)';
const GOLD_TX = '#8a7234';

const CAT_ORDER = ['Statuts', 'PV', 'Kbis', 'RBE', 'Cession', 'Autres'];
const CAT_COLOR = { Statuts: '#0F6E56', PV: '#534AB7', Kbis: '#185FA5', RBE: '#854F0B', Cession: '#A32D2D', Autres: '#5F5E5A' };

function classify(subcategory) {
  const s = (subcategory || '').toLowerCase();
  if (s.includes('statut')) return 'Statuts';
  if (s.includes('pv') || s.includes('proc') || s.includes('age')) return 'PV';
  if (s.includes('kbis') || s.includes('extrait')) return 'Kbis';
  if (s.includes('rbe') || s.includes('bénéf') || s.includes('benef') || s.includes('beneficiaire')) return 'RBE';
  if (s.includes('cession') || s.includes('cédant') || s.includes('cedant') || s.includes('parts')) return 'Cession';
  return 'Autres';
}
function flatten(attsByCat, role) {
  if (!attsByCat || typeof attsByCat !== 'object') return [];
  const out = [];
  Object.entries(attsByCat).forEach(([cat, items]) => {
    if (!Array.isArray(items)) return;
    if (role === 'client' && cat === 'lexback_internal') return;
    items.forEach(it => out.push(it));
  });
  return out;
}

/**
 * Poste de travail dossier — plein écran. Remplace l'ancien volet de détail.
 *  gauche  : contexte (actions, propriétés, éléments manquants, discussion client)
 *  centre  : document sélectionné + commentaires de la pièce (partagés client)
 *  droite  : liste des pièces + dépôt
 */
export default function DossierWorkspace({
  dossier, onClose, onUpdate, canEdit, currentUserId, currentUserRole, clientCanModify,
}) {
  const canModifyBasics = canEdit || clientCanModify;
  const [docs, setDocs] = useState([]);
  const [selId, setSelId] = useState(null);
  const [notes, setNotes] = useState([]);        // toutes les notes du dossier
  const [loading, setLoading] = useState(true);
  const [chatDraft, setChatDraft] = useState('');
  const [commentDraft, setCommentDraft] = useState('');
  const [elements, setElements] = useState('');
  const [liasse, setLiasse] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendMode, setSendMode] = useState(null); // 'client' | 'signature' | null
  const readW = (k, d) => { try { const v = parseInt(localStorage.getItem(k), 10); return Number.isFinite(v) ? v : d; } catch { return d; } };
  const [leftW, setLeftW] = useState(() => readW('lexback_ws_leftW', 360));
  const [rightW, setRightW] = useState(() => readW('lexback_ws_rightW', 260));
  const leftWRef = React.useRef(leftW);
  const rightWRef = React.useRef(rightW);
  const [dragging, setDragging] = useState(false);
  const dragRef = React.useRef(null);
  const [docTypes, setDocTypes] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadType, setUploadType] = useState('');

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem('lexback_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadDocs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents/attachments/${dossier.id}`, { headers: authHeaders() });
      const atts = res.ok ? await res.json() : {};
      const flat = flatten(atts && !atts.detail ? atts : {}, currentUserRole);
      setDocs(flat);
      setSelId(prev => prev || (flat[0] ? flat[0].id : null));
    } catch { setDocs([]); }
  }, [dossier, currentUserRole, authHeaders]);

  const loadNotes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notes/${dossier.id}`, { headers: authHeaders() });
      setNotes(res.ok ? await res.json() : []);
    } catch { setNotes([]); }
  }, [dossier, authHeaders]);

  useEffect(() => {
    if (!dossier) return;
    setLoading(true);
    setElements(dossier.elements_manquants || '');
    setLiasse(dossier.inpi_depot_id || '');
    fetch(`${API_URL}/api/documents/document-types`, { headers: authHeaders() }).then(r => r.json()).then(d => setDocTypes(d?.types || [])).catch(() => {});
    Promise.all([loadDocs(), loadNotes()]).finally(() => setLoading(false));
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [dossier, loadDocs, loadNotes, onClose]);

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current; if (!d) return;
      const dx = e.clientX - d.startX;
      if (d.side === 'left') { const nv = Math.max(180, Math.min(560, d.startW + dx)); leftWRef.current = nv; setLeftW(nv); }
      else { const nv = Math.max(160, Math.min(480, d.startW - dx)); rightWRef.current = nv; setRightW(nv); }
    };
    const onUp = () => {
      if (dragRef.current) {
        try {
          localStorage.setItem('lexback_ws_leftW', String(leftWRef.current));
          localStorage.setItem('lexback_ws_rightW', String(rightWRef.current));
        } catch { /* noop */ }
      }
      dragRef.current = null; setDragging(false);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  if (!dossier) return null;

  const grouped = CAT_ORDER.map(cat => ({ cat, items: docs.filter(d => classify(d.subcategory) === cat) }));
  const selected = docs.find(d => d.id === selId) || null;
  const chatNotes = notes.filter(n => !n.document_id);
  const docComments = notes.filter(n => selId && n.document_id === selId);

  const postNote = async (content, document_id, visibility = 'shared') => {
    const body = { content, visibility };
    if (document_id) body.document_id = document_id;
    const res = await fetch(`${API_URL}/api/notes/${dossier.id}`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) await loadNotes();
  };

  const sendChat = async () => { const v = chatDraft.trim(); if (!v) return; setChatDraft(''); await postNote(v, null, 'internal'); };
  const sendComment = async () => { const v = commentDraft.trim(); if (!v || !selId) return; setCommentDraft(''); await postNote(v, selId, 'shared'); };

  const saveElements = async (val) => {
    if (val === (dossier.elements_manquants || '')) return;
    try { await dossiersAPI.update(dossier.id, { elements_manquants: val }); onUpdate && onUpdate(); }
    catch { /* noop */ }
  };

  const saveLiasse = async (val) => {
    const v = (val || '').trim();
    if (v === (dossier.inpi_depot_id || '')) return;
    try { await dossiersAPI.update(dossier.id, { inpi_depot_id: v || null }); onUpdate && onUpdate(); }
    catch { /* noop */ }
  };

  const uploadFiles = async (fileList, subcategory) => {
    const files = Array.from(fileList || []).filter(f => /\.(pdf|jpe?g|png)$/i.test(f.name));
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const url = `${API_URL}/api/documents/attachment/${dossier.id}?subcategory=${encodeURIComponent(subcategory || 'Autres')}`;
        await fetch(url, { method: 'POST', headers: authHeaders(), body: fd });
      }
      await loadDocs();
    } finally { setUploading(false); }
  };

  const addPending = (fileList) => {
    const files = Array.from(fileList || []).filter(f => /\.(pdf|jpe?g|png)$/i.test(f.name));
    if (files.length) setPendingFiles(prev => [...prev, ...files]);
  };
  const confirmUpload = async () => {
    if (!pendingFiles.length || !uploadType) return;
    await uploadFiles(pendingFiles, uploadType);
    setPendingFiles([]); setUploadType('');
  };
  const deleteDoc = async (d) => {
    if (!canEdit) return;
    if (!window.confirm(`Supprimer «\u00a0${d.nom_original}\u00a0» ?`)) return;
    try {
      await fetch(`${API_URL}/api/documents/attachment/${d.id}`, { method: 'DELETE', headers: authHeaders() });
      if (selId === d.id) setSelId(null);
      await loadDocs();
    } catch { /* noop */ }
  };

  const startDrag = (side) => (e) => { e.preventDefault(); dragRef.current = { side, startX: e.clientX, startW: side === 'left' ? leftW : rightW }; setDragging(true); };

  const isPdf = selected && ((selected.mime_type || '').includes('pdf') || /\.pdf(\?|$)/i.test(selected.nom_original || selected.download_url || ''));
  const isImg = selected && ((selected.mime_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp)(\?|$)/i.test(selected.nom_original || selected.download_url || ''));

  const statut = dossier.status_label?.name || dossier.status_label_obj?.name || dossier.status || '—';
  const greffe = dossier.greffe_label?.name || dossier.greffe || '—';

  const sectionLabel = (t) => (
    <div style={{ fontSize: '10.5px', letterSpacing: '.04em', color: '#8b8576', textTransform: 'uppercase', fontWeight: 500, margin: '0 0 7px' }}>{t}</div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(15,15,18,0.55)', display: 'flex', flexDirection: 'column', fontFamily: 'var(--font-body, sans-serif)' }}>
      {/* Barre du haut */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 16px', background: '#1f1612', color: '#fff', flexShrink: 0 }}>
        <span style={{ background: GOLD, color: '#1a1410', width: '28px', height: '28px', borderRadius: '6px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 500, fontSize: '11px', flexShrink: 0 }}>
          {isPdf ? 'PDF' : isImg ? 'IMG' : 'DOC'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {selected ? selected.nom_original : (dossier.societe_nom || dossier.reference)}
          </div>
          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>
            {dossier.societe_nom}{dossier.societe_siren ? ` · ${dossier.societe_siren}` : ''} · {statut}
          </div>
        </div>
        {selected?.download_url && (
          <a href={selected.download_url} target="_blank" rel="noopener noreferrer" title="Ouvrir dans un onglet"
             style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '17px', padding: '0 4px' }}>↗</a>
        )}
        {selected?.download_url && (
          <a href={selected.download_url} download={selected.nom_original || true} title="Télécharger"
             style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none', fontSize: '17px', padding: '0 4px' }}>⬇</a>
        )}
        <button onClick={onClose} title="Fermer (Échap)" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '30px', height: '30px', borderRadius: '7px', cursor: 'pointer', fontSize: '18px' }}>×</button>
      </div>

      {/* Corps 3 colonnes */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

        {/* GAUCHE — contexte */}
        <div style={{ width: leftW, flexShrink: 0, background: '#fafaf8', borderRight: '0.5px solid rgba(0,0,0,0.08)', overflowY: 'auto', padding: '16px' }}>
          <div style={{ fontSize: '15px', fontWeight: 500, marginBottom: '4px' }}>{dossier.societe_nom || '—'}</div>
          <span style={{ fontSize: '10.5px', color: GOLD_TX, background: 'rgba(217,168,71,0.14)', padding: '2px 8px', borderRadius: '20px' }}>{dossier.formality_type_label?.name || dossier.type || '—'}</span>

          {canModifyBasics && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', margin: '16px 0' }}>
              <button onClick={() => setSendMode('client')} style={btnStyle}>✉ Envoyer au client</button>
              <button onClick={() => setSendMode('signature')} style={btnStyle}>✍️ Pour signature</button>
            </div>
          )}

          <div style={{ marginTop: '16px' }}>
            {sectionLabel('Propriétés')}
            <div style={{ fontSize: '12px', color: '#5C6B7A', lineHeight: 1.8 }}>
              <div><span style={{ color: '#8b8576' }}>Statut · </span>{statut}</div>
              <div><span style={{ color: '#8b8576' }}>Greffe · </span>{greffe}</div>
              {dossier.tarif_gtc != null && <div><span style={{ color: '#8b8576' }}>Tarif GTC · </span>{dossier.tarif_gtc} €</div>}
              {dossier.formaliste_nom && <div><span style={{ color: '#8b8576' }}>Formaliste · </span>{dossier.formaliste_nom}</div>}
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            {sectionLabel('INPI')}
            <div style={{ fontSize: '12px', color: '#5C6B7A', lineHeight: 1.8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ color: '#8b8576' }}>N° de liasse · </span>
                {canModifyBasics ? (
                  <input
                    value={liasse}
                    onChange={e => setLiasse(e.target.value)}
                    onBlur={e => saveLiasse(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                    placeholder="J00000000000"
                    style={{ flex: 1, minWidth: 0, border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontFamily: 'monospace', outline: 'none' }}
                  />
                ) : (liasse || '—')}
              </div>
              {dossier.inpi_status_raw && <div><span style={{ color: '#8b8576' }}>Statut INPI · </span>{dossier.inpi_status_raw}</div>}
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            {sectionLabel('Éléments manquants')}
            {canModifyBasics ? (
              <textarea value={elements} onChange={e => setElements(e.target.value)} onBlur={e => saveElements(e.target.value)}
                placeholder="Pièces ou informations à fournir…" rows={3}
                style={{ width: '100%', border: '0.5px solid rgba(0,0,0,0.12)', borderLeft: '3px solid #f59e0b', borderRadius: '0 7px 7px 0', padding: '8px 9px', fontSize: '12px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', lineHeight: 1.5, background: 'rgba(245,158,11,0.05)' }} />
            ) : (
              <div style={{ fontSize: '12px', borderLeft: '3px solid #f59e0b', background: 'rgba(245,158,11,0.06)', padding: '8px 9px', borderRadius: '0 6px 6px 0' }}>{elements || '—'}</div>
            )}
          </div>

          <div style={{ marginTop: '18px' }}>
            {sectionLabel("Discussion d'équipe")}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '8px' }}>
              {chatNotes.length === 0 && <div style={{ fontSize: '11.5px', color: '#aaa49a', fontStyle: 'italic' }}>Aucun message.</div>}
              {chatNotes.map(n => (
                <div key={n.id} style={{ fontSize: '11.5px' }}>
                  <span style={{ fontWeight: 500 }}>{n.author_name || 'Message'}</span>
                  <div style={{ background: '#f1efe8', padding: '6px 9px', borderRadius: '9px', marginTop: '2px', lineHeight: 1.4 }}>{n.content}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <input value={chatDraft} onChange={e => setChatDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendChat(); }} placeholder="Message à l'équipe…"
                style={{ flex: 1, border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '7px', padding: '6px 9px', fontSize: '11.5px', outline: 'none' }} />
              <button onClick={sendChat} style={{ background: GOLD, color: '#1a1410', border: 'none', borderRadius: '7px', padding: '0 11px', cursor: 'pointer', fontSize: '13px', fontWeight: 500 }}>↑</button>
            </div>
          </div>
        </div>

        <div onMouseDown={startDrag('left')} title="Glisser pour redimensionner" style={{ width: '6px', flexShrink: 0, cursor: 'col-resize', background: dragging ? 'rgba(217,168,71,0.4)' : 'rgba(0,0,0,0.06)' }} />
        {/* CENTRE — document + commentaires de la pièce */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', background: '#3a3a3e' }}>
          <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px' }}>
            {loading ? (
              <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px' }}>Chargement…</div>
            ) : !selected ? (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px', textAlign: 'center' }}>Aucun document. Déposez une pièce à droite →</div>
            ) : selected.download_url ? (
              <div style={{ background: '#fff', borderRadius: '8px', width: '100%', height: '100%', overflow: 'hidden' }}>
                {isPdf ? (
                  <iframe src={selected.download_url} title={selected.nom_original} style={{ width: '100%', height: '100%', border: 'none', pointerEvents: dragging ? 'none' : 'auto' }} />
                ) : isImg ? (
                  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src={selected.download_url} alt={selected.nom_original} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  </div>
                ) : (
                  <div style={{ padding: '40px', color: '#5C6B7A', textAlign: 'center', fontSize: '13px' }}>Aperçu indisponible — <a href={selected.download_url} target="_blank" rel="noopener noreferrer">ouvrir</a></div>
                )}
              </div>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '13px' }}>Aperçu indisponible.</div>
            )}
          </div>

          {/* Commentaires de la pièce — partagés avec le client */}
          {selected && (
            <div style={{ background: '#fff', borderTop: '0.5px solid rgba(0,0,0,0.1)', padding: '11px 16px', maxHeight: '210px', overflowY: 'auto' }}>
              <div style={{ fontSize: '10.5px', letterSpacing: '.04em', color: '#8b8576', marginBottom: '8px' }}>
                COMMENTAIRES SUR <span style={{ color: GOLD_TX }}>{selected.nom_original}</span> <span style={{ color: '#aaa49a', textTransform: 'none' }}>· visibles par le client</span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                {docComments.length === 0 && <div style={{ fontSize: '11.5px', color: '#aaa49a', fontStyle: 'italic' }}>Aucun commentaire sur cette pièce.</div>}
                {docComments.map(n => (
                  <div key={n.id} style={{ fontSize: '12px', marginBottom: '7px' }}>
                    <span style={{ fontWeight: 500 }}>{n.author_name || 'Commentaire'}</span>
                    <div style={{ color: '#424245', lineHeight: 1.45 }}>{n.content}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '6px' }}>
                <input value={commentDraft} onChange={e => setCommentDraft(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendComment(); }}
                  placeholder="Noter un commentaire sur cette pièce…"
                  style={{ flex: 1, border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '7px', padding: '7px 9px', fontSize: '12px', outline: 'none' }} />
                <button onClick={sendComment} style={{ background: GOLD, color: '#1a1410', border: 'none', borderRadius: '7px', padding: '0 13px', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>Ajouter</button>
              </div>
            </div>
          )}
        </div>

        <div onMouseDown={startDrag('right')} title="Glisser pour redimensionner" style={{ width: '6px', flexShrink: 0, cursor: 'col-resize', background: dragging ? 'rgba(217,168,71,0.4)' : 'rgba(0,0,0,0.06)' }} />
        {/* DROITE — pièces */}
        <div style={{ width: rightW, flexShrink: 0, background: '#fafaf8', borderLeft: '0.5px solid rgba(0,0,0,0.08)', overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}>
          {sectionLabel('Documents')}
          <div style={{ flex: 1 }}>
            {grouped.filter(g => g.items.length > 0).map(g => (
              <div key={g.cat} style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '10px', color: '#8b8576', marginBottom: '3px' }}>{g.cat}</div>
                {g.items.map(d => (
                  <div key={d.id} onClick={() => setSelId(d.id)} style={{
                    cursor: 'pointer', borderRadius: '7px', padding: '7px 8px', marginBottom: '2px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    background: d.id === selId ? 'rgba(217,168,71,0.14)' : 'transparent',
                  }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: CAT_COLOR[g.cat], flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: '11.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.nom_original}</span>
                    {canEdit && (
                      <span onClick={(e) => { e.stopPropagation(); deleteDoc(d); }} title="Supprimer" style={{ color: '#c0392b', fontSize: '13px', cursor: 'pointer', flexShrink: 0, opacity: 0.7 }}>🗑</span>
                    )}
                  </div>
                ))}
              </div>
            ))}
            {docs.length === 0 && !loading && <div style={{ fontSize: '11.5px', color: '#aaa49a', fontStyle: 'italic' }}>Aucune pièce.</div>}
          </div>

          {canModifyBasics && pendingFiles.length === 0 && (
            <div
              onClick={() => document.getElementById('ws-upload-input')?.click()}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={e => { e.preventDefault(); setDragOver(false); }}
              onDrop={e => { e.preventDefault(); setDragOver(false); addPending(e.dataTransfer.files); }}
              style={{
                border: `1.5px dashed ${dragOver ? '#B8860B' : 'rgba(217,168,71,0.5)'}`,
                background: dragOver ? 'rgba(217,168,71,0.10)' : 'rgba(217,168,71,0.04)',
                borderRadius: '8px', padding: '14px 10px', textAlign: 'center', cursor: 'pointer',
                fontSize: '11px', color: GOLD_TX, marginTop: '8px',
              }}>
              📎 Glissez une pièce ici, ou cliquez
            </div>
          )}
          {canModifyBasics && pendingFiles.length > 0 && (
            <div style={{ marginTop: '8px', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '8px', padding: '10px', background: '#fff' }}>
              <div style={{ fontSize: '11px', color: '#5C6B7A', marginBottom: '6px' }}>{pendingFiles.length} fichier{pendingFiles.length > 1 ? 's' : ''} à ajouter</div>
              <select value={uploadType} onChange={e => setUploadType(e.target.value)}
                style={{ width: '100%', padding: '6px 8px', border: '0.5px solid rgba(0,0,0,0.15)', borderRadius: '6px', fontSize: '11.5px', marginBottom: '7px', background: '#fff' }}>
                <option value="">— Type de document —</option>
                {(() => {
                  const groups = {};
                  docTypes.forEach(ty => { if (currentUserRole === 'client' && ty.category === 'lexback_internal') return; (groups[ty.category] = groups[ty.category] || []).push(ty); });
                  const labels = { juridique: 'Juridique', officiel: 'Officiel', justificative: 'Justificative', lexback_internal: 'Interne' };
                  return Object.keys(groups).map(cat => (
                    <optgroup key={cat} label={labels[cat] || cat}>
                      {groups[cat].map(ty => <option key={ty.label} value={ty.label}>{ty.label}</option>)}
                    </optgroup>
                  ));
                })()}
              </select>
              <div style={{ display: 'flex', gap: '6px' }}>
                <button onClick={() => { setPendingFiles([]); setUploadType(''); }} style={{ flex: 1, border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '6px', padding: '6px', fontSize: '11.5px', background: '#fff', cursor: 'pointer', color: '#8b8576' }}>Annuler</button>
                <button onClick={confirmUpload} disabled={!uploadType || uploading} style={{ flex: 1, border: 'none', borderRadius: '6px', padding: '6px', fontSize: '11.5px', fontWeight: 500, cursor: (!uploadType || uploading) ? 'default' : 'pointer', background: (!uploadType || uploading) ? '#e7ddc6' : GOLD, color: (!uploadType || uploading) ? '#aaa49a' : '#1a1410' }}>{uploading ? 'Envoi…' : 'Ajouter'}</button>
              </div>
            </div>
          )}
          <input id="ws-upload-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
            onChange={e => { addPending(e.target.files); e.target.value = ''; }} />
        </div>
      </div>

      {sendMode && (
        <SendDocumentsModal dossier={dossier} mode={sendMode} onClose={() => setSendMode(null)} onSent={() => setSendMode(null)} />
      )}
    </div>
  );
}

const btnStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
  padding: '9px 10px', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '8px',
  fontSize: '12px', fontWeight: 500, color: '#1d1d1f', background: '#fff', cursor: 'pointer',
};
